import React from 'react';
import { ShieldIcon, ArrowLeftIcon } from './icons';
import { usePlanStore } from '../store/usePlanStore';

interface Props {
    publicMode?: boolean;
}

export default function TermsPage({ publicMode }: Props = {}) {
    const goToDashboard = usePlanStore((s) => s.goToDashboard);

    const handleBack = () => {
        if (publicMode) {
            window.location.href = '/';
        } else {
            goToDashboard();
        }
    };

    return (
        <div className="max-w-3xl mx-auto animate-fade-in">
            <button
                onClick={handleBack}
                className="mb-4 flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
            >
                <ArrowLeftIcon size={14} /> Powrót
            </button>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-950 flex items-center justify-center">
                        <ShieldIcon size={22} className="text-brand-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Regulamin serwisu APEXBuilder</h1>
                        <p className="text-xs text-slate-400">Ostatnia aktualizacja: {new Date().toLocaleDateString('pl-PL')}</p>
                    </div>
                </div>

                <div className="prose dark:prose-invert prose-sm max-w-none space-y-4 text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">§1. Postanowienia ogólne</h2>
                    <ol className="list-decimal list-inside space-y-1">
                        <li>Serwis APEXBuilder (dalej: „Serwis") jest niekomercyjnym projektem studenckim służącym do tworzenia planów treningowych.</li>
                        <li>Korzystanie z Serwisu jest całkowicie bezpłatne.</li>
                        <li>Serwis nie stanowi podmiotu gospodarczego i nie świadczy usług komercyjnych.</li>
                        <li>Rejestracja i korzystanie z Serwisu oznacza akceptację niniejszego Regulaminu.</li>
                    </ol>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">§2. Definicje</h2>
                    <ol className="list-decimal list-inside space-y-1">
                        <li><strong>Użytkownik</strong> - osoba zarejestrowana w Serwisie.</li>
                        <li><strong>Trener</strong> - Użytkownik z rolą „trener", tworzący plany i ankiety dla klientów.</li>
                        <li><strong>Klient</strong> - Użytkownik z rolą „klient" lub osoba wypełniająca ankietę.</li>
                        <li><strong>Plan treningowy</strong> - program ćwiczeń stworzony przez Użytkownika w Serwisie.</li>
                    </ol>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">§3. Odpowiedzialność za plany treningowe</h2>
                    <ol className="list-decimal list-inside space-y-2">
                        <li><strong>Serwis jest wyłącznie narzędziem.</strong> APEXBuilder udostępnia narzędzia do budowania planów treningowych, ale nie jest certyfikowanym doradcą treningowym ani medycznym.</li>
                        <li><strong>Trener ponosi pełną odpowiedzialność</strong> za plany treningowe tworzone w Serwisie i udostępniane klientom. Trener gwarantuje, że posiada odpowiednią wiedzę i/lub kwalifikacje do tworzenia programów treningowych.</li>
                        <li>Sugestie generowane przez „Asystenta AI" i „Analizę ankiety" mają charakter informacyjny i orientacyjny. Nie stanowią porady medycznej, dietetycznej ani treningowej.</li>
                        <li><strong>Użytkownik korzysta z planów treningowych na własną odpowiedzialność.</strong> Przed rozpoczęciem nowego programu treningowego zaleca się konsultację z lekarzem.</li>
                        <li>Autor Serwisu nie ponosi odpowiedzialności za jakiekolwiek szkody wynikłe z korzystania z planów treningowych stworzonych w Serwisie.</li>
                    </ol>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">§4. Konto użytkownika</h2>
                    <ol className="list-decimal list-inside space-y-1">
                        <li>Rejestracja wymaga podania adresu e-mail, imienia i hasła oraz weryfikacji adresu e-mail.</li>
                        <li>Użytkownik zobowiązuje się do podania prawdziwych danych i zachowania poufności hasła.</li>
                        <li>Użytkownik może w każdej chwili zażądać usunięcia swojego konta, kontaktując się z administratorem.</li>
                    </ol>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">§5. Zasady korzystania</h2>
                    <ol className="list-decimal list-inside space-y-1">
                        <li>Zakazane jest korzystanie z Serwisu w sposób naruszający prawo lub prawa osób trzecich.</li>
                        <li>Zakazane jest udostępnianie treści obraźliwych, wulgarnych lub niezgodnych z prawem.</li>
                        <li>Administrator zastrzega sobie prawo do usunięcia konta naruszającego Regulamin.</li>
                    </ol>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">§6. Dostępność serwisu</h2>
                    <ol className="list-decimal list-inside space-y-1">
                        <li>Serwis jest hostowany na darmowych platformach (Render.com, Vercel). Autor nie gwarantuje ciągłości działania ani dostępności.</li>
                        <li>Autor zastrzega sobie prawo do zaprzestania działania Serwisu bez wcześniejszego powiadomienia.</li>
                        <li>Użytkownik powinien regularnie eksportować swoje dane (plany treningowe) jako kopie zapasowe.</li>
                    </ol>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">§7. Własność intelektualna</h2>
                    <ol className="list-decimal list-inside space-y-1">
                        <li>Plany treningowe stworzone przez Użytkownika pozostają jego własnością.</li>
                        <li>Kod źródłowy i design Serwisu są własnością autora projektu.</li>
                    </ol>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">§8. Postanowienia końcowe</h2>
                    <ol className="list-decimal list-inside space-y-1">
                        <li>Regulamin może być zmieniony w każdym czasie. Kontynuacja korzystania z Serwisu po zmianach oznacza ich akceptację.</li>
                        <li>W sprawach nieuregulowanych niniejszym Regulaminem stosuje się przepisy prawa polskiego.</li>
                    </ol>
                </div>
            </div>
        </div>
    );
}
